import { create } from "zustand";
import { devtools } from "zustand/middleware";
import config from "../config";
import {
  apiResult,
  Contact,
  ContactsDBObject,
  FilterOption,
} from "../interfaces";
import { adaptItem, runChatList } from "../utils/contacts";

import { useAppStore } from "./app";

interface ContactStoreState {
  contactsDbObject: ContactsDBObject;
  contactList: Contact[];
  searchActive: boolean;
  currentContact: string;
  loading: boolean;

  // Escalated count
  counters: {
    escalated: number | null;
  };

  // Actions available for filters and rows
  availableActions: string[];

  // Filters
  filters: FilterOption[];
  setLoading: (loading: boolean) => void;
  updateContactsDb: (
    contacts: apiResult[],
    options?: { isWebChat?: boolean; isSearch?: boolean }
  ) => void;
  setSearchActive: (searchActive: boolean) => void;
  setFilters: (filters: FilterOption[]) => void;
  insertSentMessage: () => void;
  modifyEnabled: () => void;
  changeContactStatus: () => void;
  readMessages: () => void;
  updateCounters: (counters: { escalated: number }) => void;
  setAvailableActions: (actions: string[]) => void;
}

const useContactsStore = create(
  devtools<ContactStoreState>((set, get) => ({
    contactsDbObject: {},
    contactList: [],
    currentContact: "",
    loading: true,
    counters: {
      escalated: 0,
    },
    availableActions: [],
    searchActive: false,
    filters: [
      {
        label: "version",
        condition: "or",
        type: "number",
        isActive: false,
        value: 0,
        isVisible: false,
      },
      {
        label: "escalated",
        condition: "or",
        type: "toggle",
        isActive: false,
        value: true,
      },
      {
        label: "enabled",
        condition: "or",
        type: "toggle",
        isActive: false,
        value: false,
      },
      {
        label: "isManual",
        condition: "or",
        type: "toggle",
        isActive: false,
        value: true,
      },
      {
        label: "actions",
        type: "list",
        condition: "or",
        isActive: false,
        value: [],
        isVisible: false,
      },
      {
        label: "quality",
        type: "list",
        condition: "and",
        isActive: false,
        value: [],
        isVisible: false,
      },
      {
        label: "companies",
        type: "list",
        condition: "and",
        isActive: false,
        value: [],
        isVisible: false,
      },
      {
        label: "users",
        type: "list",
        condition: "and",
        isActive: false,
        value: [],
        isVisible: false,
      },
      {
        label: "search",
        type: "search",
        isActive: false,
        value: "",
        isVisible: false,
      },
      {
        label: "bucket",
        type: "bucket",
        isActive: false,
        value: "",
        isVisible: false,
      },
    ],

    setLoading: (loading) => set({ loading }),
    updateCounters: (counters: { escalated: number }) => {
      set({
        counters: {
          ...get().counters,
          escalated: counters.escalated || 0,
        },
      });
    },
    updateContactsDb: (contacts: apiResult[], options) => {
      const currentContactsDbObject = get().contactsDbObject;
      const updatedContactsDbObject = { ...currentContactsDbObject };
      const isSearchResult = options?.isSearch || false;

      // Reset isSearch flag for updatedContactsDbObject
      if (isSearchResult) {
        Object.values(updatedContactsDbObject).forEach((value) => {
          value.contact.isSearch = false;
        });
      }
      const user = useAppStore.getState().user;
      contacts.forEach((c) => {
        const contact = adaptItem(c);
        const existing = currentContactsDbObject[contact.contact.id];
        updatedContactsDbObject[contact.contact.id] = {
          ...contact,
          contact: {
            ...contact?.contact,
            isSearch: options?.isSearch || false,
          },
          bookings: c.bookings ? contact.bookings : existing?.bookings ?? [],
          messages: c.messages ? contact.messages : existing?.messages ?? [],
          toolCalls: c.toolCalls
            ? contact.toolCalls
            : existing?.toolCalls ?? [],
          mine:
            contact.assignations?.some(
              (a: apiResult) => a.email === user?.email
            ) || false,
        };
      });

      const filters = get().filters;
      const { list } = runChatList(updatedContactsDbObject, { filters });
      set({
        contactsDbObject: updatedContactsDbObject,
        contactList: list,
      });
    },
    setFilters: (filters: FilterOption[]) => {
      const newFilter = filters.map((f) => {
        return {
          ...f,
          value: f.label === "version" ? (f.value as number) + 1 : f.value,
        };
      });

      set({
        filters: newFilter,
        //contactsDbObject: {},
        contactList: [],
      });
    },

    setAvailableActions: (actions: string[]) => {
      set({ availableActions: actions });
    },
    setSearchActive: (searchActive: boolean) => set({ searchActive }),
    insertSentMessage: () => {},
    modifyEnabled: () => {},
    changeContactStatus: () => {},
    readMessages: () => {},
  }))
);

export default useContactsStore;
